/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */

import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, TextField } from "@mui/material";

import { useStyles } from "./styles";
import { MESSAGES, TITLES } from "./constants";
import Loader from "../../shared/loader/Loader";
import FormDialog from "../../shared/dialog/FormDialog";
import { selectError } from "../../store/error/errorSelector";
import { selectLoader } from "../../store/loader/loaderSelector";
import { ErrorMessage } from "../../shared/ErrorMessage/ErrorMessage";
import { CustomTooltip } from "../../shared/customTooltip/CustomTooltip";
import { selectSelectedCustomer } from "../../store/customer/customerSelector";
import { setSnackBarDisplay } from "../../store/snackBar/snackBarActionCreator";
import { createSubEnterPrise, editSubEnterPrise } from "../../store/subEnterprise/subEnterprise";
import {
  ISubEnterpriseRequest,
  IFormValuesSubEnterPrise,
  ICreateSubEnterpriseErrorObj,
  IAddSubEnterpirseDialogProps,
} from "../../models/Customers/subEnterprise";
import { showErrorAction } from "../../store/error/errorActionCreator";
import { hideLoaderAction } from "../../store/loader/loaderActionCreator";

const AddSubEnterpriseDialog = (props: IAddSubEnterpirseDialogProps) => {
  const formDefaultValues: ISubEnterpriseRequest = {
    id: "",
    name: "",
    costCenter: "",
    displayName: "",
    accountKey: "",
  };

  const formErrorDefaultValues = {
    nameError: false,
    costCenterError: false,
    displayNameError: false,
    accountKeyError: false,
    errorHelperTextName: "",
    errorHelperTextDisplayName: "",
    errorHelperTextCostCenter: "",
    errorHelperTextAccountKey: "",
  };

  const snackBarAlertDefaultProps = {
    show: true,
    autoHideDuration: 5000,
    message: "Customer added!",
    severity: "success" as "success" | "error",
  };
  const classes = useStyles();
  const dispatch = useDispatch();

  const loading = useSelector(selectLoader);
  const errorMesaage = useSelector(selectError);
  const selectedCustomer = useSelector(selectSelectedCustomer);

  const [formErrorState, updateFormError] = useState(formErrorDefaultValues);
  const [formValues, updateFormValues] = useState<ISubEnterpriseRequest>(formDefaultValues);

  const handleClose = () => {
    props.onClose();
  };

  useEffect(() => {
    updateFormValues({ ...formDefaultValues });
    updateFormError({ ...formErrorDefaultValues });
    dispatch(hideLoaderAction());
    dispatch(showErrorAction({ message: '' }))
  }, [props.isOpen]);

  useEffect(() => {
    updateFormValues({ ...formDefaultValues });
    updateFormError({ ...formErrorDefaultValues });
    if (props.isEdit) {
      updateFormValues(props.subEnterpriseData);
    }
  }, [props.isEdit, props.subEnterpriseData]);

  const updateFormFields = (event: { target: { name: string; value: string } }) => {
    const { name, value } = event.target;
    const trimmedValue = value.trim();
    if (name === 'name') {
      let errorObj = { nameError: false, errorHelperTextName: '' };
      if (trimmedValue === '') {
        updateFormValues({ ...formValues, name: '' });
      } else if (trimmedValue.length > 20) {
        errorObj = {
          nameError: true,
          errorHelperTextName: MESSAGES.MAX_ACCOUNT_NAME_LENGTH,
        };
      } else if (!MESSAGES.ACCOUNT_NAME_REGEX.test(trimmedValue)) {
        errorObj = {
          nameError: true,
          errorHelperTextName: MESSAGES.ACCOUNT_NAME_ALLOWED_CHARACTERS,
        };
      } else {
        updateFormValues({ ...formValues, name: trimmedValue });
      }
      updateFormError({ ...formErrorState, ...errorObj });
    } else if (name === 'accountKey') {
      let errorObj = { accountKeyError: false, errorHelperTextAccountKey: '' };

      if (trimmedValue === '') {
        updateFormValues({ ...formValues, accountKey: '' });
      } else if (trimmedValue.length > 8) {
        errorObj = {
          accountKeyError: true,
          errorHelperTextAccountKey: MESSAGES.MAX_ACCOUNT_KEY_LENGTH,
        };
      } else if (!MESSAGES.ACCOUNT_KEY_REGEX.test(trimmedValue)) {
        errorObj = {
          accountKeyError: true,
          errorHelperTextAccountKey: MESSAGES.ACCOUNT_KEY_ALLOWED_CHARACTERS,
        };
      } else {
        updateFormValues({ ...formValues, accountKey: trimmedValue });
      }

      updateFormError({ ...formErrorState, ...errorObj });
    } else {
      updateFormValues({ ...formValues, [name]: value });
      updateFormError({ ...formErrorState, ...formErrorDefaultValues });
    }
  };

  const validateRequired = (values: IFormValuesSubEnterPrise) => {
    const errorObj: ICreateSubEnterpriseErrorObj = {};

    if (!values.name) {
      errorObj.nameError = true;
      errorObj.errorHelperTextName = MESSAGES.ACCOUNT_NAME_REQUIRED;
    }
    if (!values.displayName) {
      errorObj.displayNameError = true;
      errorObj.errorHelperTextDisplayName = MESSAGES.DISPLAY_NAME_REQUIRED;
    }
    if (!values.costCenter) {
      errorObj.costCenterError = true;
      errorObj.errorHelperTextCostCenter = MESSAGES.COST_CENTER_REQUIRED;
    } else if (!(+values.costCenter)) {
      errorObj.costCenterError = true;
      errorObj.errorHelperTextCostCenter = MESSAGES.COST_CENTER_INVALID;
    }
    if (!values.accountKey) {
      errorObj.accountKeyError = true;
      errorObj.errorHelperTextAccountKey = MESSAGES.ACCOUNT_KEY_REQUIRED;
    }

    if (Object.keys(errorObj).length > 0) {
      updateFormError({ ...formErrorState, ...errorObj });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    let isSucccess = false;
    if (validateRequired(formValues)) {
      const payload = {
        name: formValues.name.toLowerCase(),
        displayName: formValues.displayName,
        costCenter: formValues.costCenter,
        customerId: selectedCustomer.id,
        id: props.subEnterpriseData.id,
        accountKey: formValues.accountKey
      };
      if (props.isEdit) {
        isSucccess = await editSubEnterPrise(dispatch, payload);
      } else {
        isSucccess = await createSubEnterPrise(dispatch, payload);
      }
      if (isSucccess) {
        dispatch(
          setSnackBarDisplay({
            ...snackBarAlertDefaultProps,
            show: true,
            message: MESSAGES.ACCOUNT_CREATED,
          })
        );
        handleClose();
      }
    } else {
      return false;
    }
  };

  return (
    <Fragment>
      <FormDialog
        dialogAction1={
          <Button
            color="primary"
            disableElevation
            name="cancelBtn"
            onClick={handleClose}
            variant="outlined"
          >
            {TITLES.CANCEL}
          </Button>
        }
        dialogAction2={
          <Button
            color="primary"
            disableElevation
            disabled={loading}
            name="createCustomerBtn"
            onClick={handleSubmit}
            variant="contained"
          >
            {TITLES.SAVE}
          </Button>
        }
        openModal={props.isOpen}
        handleClose={handleClose}
        dialogTitle={props.isEdit ? TITLES.EDIT_ACCOUNT : TITLES.ADD_ACCOUNT}
      >
        <Box className={classes.modalFormContent}>
          <TextField
            required
            fullWidth
            id="name"
            name="name"
            variant="standard"
            value={formValues.name}
            onChange={updateFormFields}
            inputProps={{ maxLength: 20 }}
            error={formErrorState.nameError}
            className={classes.modalTextBox}
            label={TITLES.ACCOUNT_NAME}
            disabled={props.isEdit ? true : loading}
            helperText={formErrorState.errorHelperTextName}
          />
          <CustomTooltip title={MESSAGES.ACCOUNT_NAME_TOOLTIP}></CustomTooltip>
        </Box>
        <Box className={classes.modalFormContent}>
          <TextField
            required
            fullWidth
            id="displayName"
            name="displayName"
            variant="standard"
            disabled={loading}
            onChange={updateFormFields}
            value={formValues.displayName}
            inputProps={{ maxLength: 32 }}
            className={classes.modalTextBox}
            error={formErrorState.displayNameError}
            label={TITLES.ACCOUNT_DISPLAY_NAME}
            helperText={formErrorState.errorHelperTextDisplayName}
          />
          <CustomTooltip title={MESSAGES.ACCOUNT_DISPLAY_NAME_TOOLTIP}></CustomTooltip>
        </Box>
        <Box className={classes.modalFormContent}>
          <TextField
            required
            fullWidth
            id="costCenter"
            name="costCenter"
            variant="standard"
            onChange={updateFormFields}
            value={formValues.costCenter}
            className={classes.modalTextBox}
            error={formErrorState.costCenterError}
            label={TITLES.ACCOUNT_COST_CENTER}
            disabled={props.isEdit ? true : loading}
            helperText={formErrorState.errorHelperTextCostCenter}
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 10 }}
          />
          <CustomTooltip title={MESSAGES.ACCOUNT_COST_CENTER_TOOLTIP}></CustomTooltip>
        </Box>
        <Box className={classes.modalFormContent}>
          <TextField
            required
            fullWidth
            id="accountKey"
            name="accountKey"
            variant="standard"
            onChange={updateFormFields}
            value={formValues.accountKey}
            error={formErrorState.accountKeyError}
            className={classes.modalTextBox}
            label={TITLES.ACCOUNT_KEY}
            disabled={props.isEdit ? true : loading}
            helperText={formErrorState.errorHelperTextAccountKey}
          />
          <CustomTooltip title={MESSAGES.ACCOUNT_KEY_TOOLTIP}></CustomTooltip>
        </Box>
        {/* {errorMesaage && <ErrorMessage message={errorMesaage.message} />} */}
        <Loader showLoader={loading} isCenter />
      </FormDialog>
    </Fragment>
  );
};

export default AddSubEnterpriseDialog;
